import apiClient from './apiClient';

// Flag to determine if we should use mock data
const USE_MOCK_DATA = process.env.REACT_APP_USE_MOCK_DATA === 'true';

/**
 * Helper function to handle errors
 * @param {object} error - Error object
 * @returns {Error} - Formatted error
 */
const handleError = (error) => {
  const message =
    error.response?.data?.error?.message ||
    error.response?.data?.message ||
    error.message;
  if (message) {
    return new Error(message);
  }
  return error;
};

// Get system info
export const getSystemInfo = async () => {
  if (USE_MOCK_DATA) {
    // Mock response
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          data: {
            status: 'online',
            version: '1.0.0',
            lastUpdated: new Date().toISOString(),
            uptime: '8 days, 14 hours, 36 minutes',
            server: {
              cpu: 12,
              memory: 45,
              disk: 22
            },
            database: {
              status: 'online'
            },
            storage: {
              used: '1.2 GB',
              total: '10 GB',
              percent: 12
            },
            users: {
              active: 32,
              total: 125
            },
            license: {
              type: 'Business',
              expiry: '2025-12-31'
            }
          }
        });
      }, 500);
    });
  }
  
  try {
    const response = await apiClient.get('/system/info');
    return { data: response.data.data, message: response.data.message };
  } catch (error) {
    throw handleError(error);
  }
};

// Get system logs with filters
export const getSystemLogs = async (filters = {}, options = {}) => {
  const { 
    page = 1, 
    limit = 10, 
    type = 'all', 
    level = 'all', 
    search = '',
    startDate = '',
    endDate = '',
    sortBy = 'timestamp',
    sortOrder = 'DESC'
  } = filters;
  
  if (USE_MOCK_DATA) {
    // Mock response with random logs
    return new Promise((resolve) => {
      setTimeout(() => {
        // Generate mock logs based on filters
        const types = ['system', 'auth', 'user', 'api', 'database'];
        const levels = ['error', 'warning', 'info', 'debug'];
        const messages = [
          'User login successful',
          'Failed login attempt',
          'System backup completed',
          'Database connection error',
          'API request timeout',
          'User password reset',
          'Scheduled task completed',
          'Email notification sent',
          'File upload error',
          'User session expired'
        ];
        const users = ['admin@example.com', 'system', 'employee@example.com'];
        
        let filteredLogs = [];
        
        // Create 50 mock logs
        for (let i = 0; i < 50; i++) {
          const logType = types[Math.floor(Math.random() * types.length)];
          const logLevel = levels[Math.floor(Math.random() * levels.length)];
          
          // Skip this log if it doesn't match the type filter
          if (type !== 'all' && logType !== type) continue;
          
          // Skip this log if it doesn't match the level filter
          if (level !== 'all' && logLevel !== level) continue;
          
          const message = messages[Math.floor(Math.random() * messages.length)];
          const user = users[Math.floor(Math.random() * users.length)];
          
          // Skip this log if it doesn't match the search filter
          if (search && !message.toLowerCase().includes(search.toLowerCase()) && 
              !user.toLowerCase().includes(search.toLowerCase())) {
            continue;
          }
          
          // Generate a random date within the last week
          const date = new Date();
          date.setDate(date.getDate() - Math.floor(Math.random() * 7)); // Random days ago (0-6)
          date.setHours(date.getHours() - Math.floor(Math.random() * 24)); // Random hours ago
          
          filteredLogs.push({
            id: i + 1,
            timestamp: date.toISOString(),
            type: logType,
            level: logLevel,
            message,
            user,
            metadata: {}
          });
        }
        
        // Sort logs based on sort parameters
        filteredLogs.sort((a, b) => {
          if (sortOrder === 'ASC') {
            return a[sortBy] > b[sortBy] ? 1 : -1;
          } else {
            return a[sortBy] < b[sortBy] ? 1 : -1;
          }
        });
        
        // Apply pagination
        const totalLogs = filteredLogs.length;
        const totalPages = Math.ceil(totalLogs / limit);
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedLogs = filteredLogs.slice(startIndex, endIndex);
        
        resolve({
          data: {
            logs: paginatedLogs,
            pagination: {
              total: totalLogs,
              page: parseInt(page),
              limit: parseInt(limit),
              totalPages
            }
          }
        });
      }, 500);
    });
  }
  
  // Convert filters to query parameters
  const params = new URLSearchParams();
  params.append('page', page);
  params.append('limit', limit);
  if (type !== 'all') params.append('type', type);
  if (level !== 'all') params.append('level', level);
  if (search) params.append('search', search);
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  for (const key of ['workDate', 'action', 'requestId', 'actorUserId', 'employeeId', 'customerId', 'timeEntryId']) {
    if (filters[key] !== undefined && filters[key] !== '') params.append(key, filters[key]);
  }
  params.append('sortBy', sortBy);
  params.append('sortOrder', sortOrder);
  
  try {
    const response = await apiClient.get(`/system/logs?${params.toString()}`, options);
    return { data: response.data.data, message: response.data.message };
  } catch (error) {
    throw handleError(error);
  }
};

// Clear system logs
export const clearSystemLogs = async (filters = {}) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          data: {
            message: 'Successfully cleared system logs',
            count: 15
          }
        });
      }, 500);
    });
  }
  
  try {
    const response = await apiClient.post('/system/logs/clear', filters);
    return { data: response.data.data, message: response.data.message };
  } catch (error) {
    throw handleError(error);
  }
};

// Get system metrics
export const getSystemMetrics = async () => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      setTimeout(() => {
        // Generate mock time entries data
        const timeEntriesData = [];
        const today = new Date();
        
        for (let i = 29; i >= 0; i--) {
          const date = new Date(today);
          date.setDate(date.getDate() - i);
          
          timeEntriesData.push({
            date: date.toISOString().split('T')[0],
            count: Math.floor(Math.random() * 50) + 10
          });
        }
        
        // Generate mock user growth data
        const userGrowthData = [];
        const currentMonth = today.getMonth();
        const currentYear = today.getFullYear();
        
        for (let i = 0; i < 12; i++) {
          const month = (currentMonth - i + 12) % 12;
          const year = currentYear - Math.floor((currentMonth - i + 12) / 12);
          
          userGrowthData.unshift({
            month: `${year}-${String(month + 1).padStart(2, '0')}`,
            count: Math.floor(Math.random() * 20) + 5
          });
        }
        
        resolve({
          data: {
            timeEntriesData,
            userGrowthData,
            entityCounts: {
              users: 125,
              customers: 48,
              employees: 85,
              timeEntries: 3254
            },
            systemHealth: {
              cpuLoad: 12.5,
              memoryUsage: 45,
              diskUsage: 22,
              responseTime: 150
            }
          }
        });
      }, 500);
    });
  }
  
  try {
    const response = await apiClient.get('/system/metrics');
    return { data: response.data.data, message: response.data.message };
  } catch (error) {
    throw handleError(error);
  }
};

// Restart system
export const restartSystem = async () => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          data: {
            message: 'System restart initiated',
            status: 'restarting',
            estimatedTimeToRestart: '30 seconds'
          }
        });
      }, 1000);
    });
  }
  
  try {
    const response = await apiClient.post('/system/restart');
    return { data: response.data.data, message: response.data.message };
  } catch (error) {
    throw handleError(error);
  }
};

export const getSystemLog = async (id, options = {}) => {
  try {
    const response = await apiClient.get(`/system/logs/${encodeURIComponent(id)}`, options);
    return { data: response.data.data };
  } catch (error) { throw handleError(error); }
};

export const exportSystemLogs = async (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (!['page', 'limit'].includes(key) && value !== undefined && value !== '' && value !== 'all') params.set(key, value);
  });
  try {
    // JSON is deliberately parsed so the UI can disclose the server's export bound before download.
    const response = await apiClient.get(`/system/logs/export?${params.toString()}`, { timeout: 60000 });
    return { data: response.data };
  } catch (error) { throw handleError(error); }
};
